import apiClient from './client';

export interface DashboardData {
  country_id: number;
  country_name: string;
  lens_id: number;
  scenario_type: string;
  total_economic_value: number;
  economic_growth: number;
  people_impacted: number;
  coverage_percentage: number;
  avg_time_saved: number;
  total_metrics: number;
  clusters: ClusterData[];
}

export interface ClusterData {
  id: string;
  name: string;
  metrics_count: number;
  economic_value: number;
  time_saved?: number;
  people_impacted: number;
  progress: number;
}

export interface MetricDetail {
  id: string;
  name: string;
  definition: string;
  value: number;
  unit: string;
  type: string;
}

export interface ClusterMetricsResponse {
  cluster_code: string;
  cluster_name: string;
  country_id: number;
  scenario: string;
  metrics: MetricDetail[];
}

export interface TemporalDistributionResponse {
  cluster_id: string;
  country_id: number;
  yearly_percentages: Array<{
    year: number;
    percentage: number;
  }>;
  is_default: boolean;
}

export interface NonAggregatableMetric {
  id: string;
  name: string;
  value: string;
  change: string;
  unit: string;
  definition?: string;
}

export interface NonAggregatableCluster {
  code: string;
  name: string;
  metrics: NonAggregatableMetric[];
}

export interface NonAggregatableMetricsResponse {
  totalCount: number;
  clusters: NonAggregatableCluster[];
  countryId: number;
}

class DashboardApi {
  // Get dashboard data for a country, lens, and scenario
  async getDashboardData(
    countryId: number,
    lensId: number,
    scenarioType: 'pessimistic' | 'baseline' | 'optimistic'
  ): Promise<DashboardData> {
    try {
      // Now the dashboard endpoint follows the standard response format
      const response = await apiClient.get<DashboardData>('/metrics/dashboard', {
        params: {
          country_id: countryId,
          lens_id: lensId,
          scenario_type: scenarioType
        }
      });
      return response;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  // Get detailed metrics for a specific cluster
  async getClusterMetricsDetails(
    countryId: number,
    clusterCode: string,
    scenarioType: 'pessimistic' | 'baseline' | 'optimistic'
  ): Promise<ClusterMetricsResponse> {
    try {
      const response = await apiClient.get<ClusterMetricsResponse>(`/metrics/dashboard/cluster/${clusterCode}/metrics`, {
        params: {
          country_id: countryId,
          scenario_type: scenarioType
        }
      });
      return response;
    } catch (error) {
      console.error('Error fetching cluster metrics details:', error);
      throw error;
    }
  }

  // Get temporal distribution for a cluster
  async getTemporalDistribution(
    clusterId: string,
    countryId?: number
  ): Promise<TemporalDistributionResponse> {
    try {
      const params: any = { cluster_id: clusterId };
      if (countryId) {
        params.country_id = countryId;
      }
      
      const response = await apiClient.get<TemporalDistributionResponse>('/metrics/dashboard/temporal-distribution', {
        params
      });
      return response;
    } catch (error) {
      console.error('Error fetching temporal distribution:', error);
      throw error;
    }
  }

  // Get non-aggregatable metrics for a country
  async getNonAggregatableMetrics(
    countryId: number
  ): Promise<NonAggregatableMetricsResponse> {
    try {
      const response = await apiClient.get<NonAggregatableMetricsResponse>(`/metrics/non-aggregatable/${countryId}`);
      return response;
    } catch (error) {
      console.error('Error fetching non-aggregatable metrics:', error);
      throw error;
    }
  }
}

const dashboardApi = new DashboardApi();
export default dashboardApi;